import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Form, Dropdown, Modal } from 'react-bootstrap';
import { FaTimesCircle } from "react-icons/fa";
import { IoEye } from "react-icons/io5";
import { MdOutlineNotInterested } from "react-icons/md";
import './rejected-leave.css';
import LeaveLayout from '../../components/LeaveLayout.jsx';

export default function RejectedLeave() {
  const [showModal, setShowModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [tableData, setTableData] = useState([]);        // Always start with empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Format Date Function
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Intl.DateTimeFormat('en-US', options).format(new Date(dateString));
    } catch {
      return "-";
    }
  };

  // Fetch Rejected Leaves
  useEffect(() => {
    const fetchRejectedLeaves = async () => {
      setLoading(true);
      setError(null);

      try {
        let url = 'https://backend.ems.hiradentallab.com/api/leavesmanual/rejected-leaves';

        // Only add search if user typed something
        if (searchQuery && searchQuery.trim() !== '') {
          url += `?search=${encodeURIComponent(searchQuery.trim())}`;
        }

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Server Error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        // 🔥 Important Fix: Handle different possible response formats
        let leavesArray = [];

        if (Array.isArray(data)) {
          leavesArray = data;
        } else if (data && Array.isArray(data.rejectedLeaves)) {
          leavesArray = data.rejectedLeaves;
        } else if (data && Array.isArray(data.data)) {
          leavesArray = data.data;
        } else if (data && typeof data === 'object') {
          // Fallback: try to find any array in the response
          leavesArray = Object.values(data).find(val => Array.isArray(val)) || [];
        }

        setTableData(leavesArray);
      } catch (err) {
        console.error("Fetch Error:", err);
        setError(err.message);
        setTableData([]);        // Reset to empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchRejectedLeaves();
  }, [searchQuery]);

  // Handle Eye Click (Modal)
  const handleEyeClick = (rowData) => {
    setSelectedRowData(rowData);
    setShowModal(true);
  };

  // Handle Search
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Pagination Logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = tableData.slice(indexOfFirstRow, indexOfLastRow); // Safe now because tableData is always array

  const totalPages = Math.ceil(tableData.length / rowsPerPage);

  // Handle Page Change
  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <LeaveLayout>
        <div className="text-center mt-5">
          <h4>Loading Rejected Leaves...</h4>
        </div>
      </LeaveLayout>
    );
  }

  if (error) {
    return (
      <LeaveLayout>
        <div className="alert alert-danger mt-4">
          Error: {error}
        </div>
      </LeaveLayout>
    );
  }

  return (
    <LeaveLayout>
      <div>
        <h1 className="rejected-leave-header">
          <FaTimesCircle className="rejected-leave-icon" />
          Rejected Leaves
        </h1>
        <hr className="rejected-leave-divider" />

        <div className="rejected-leave-content">
          <div className="filter-container">
            <Col xs={12} md={6} className="filter-left">
              <Dropdown className="d-inline mx-2">
                <Dropdown.Toggle id="dropdown-autoclose-inside">
                  Show {rowsPerPage} Entries
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => { setRowsPerPage(15); setCurrentPage(1); }}>15</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setRowsPerPage(25); setCurrentPage(1); }}>25</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setRowsPerPage(50); setCurrentPage(1); }}>50</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={12} md={6} className="filter-right">
              <Form>
                <Form.Control
                  type="search"
                  placeholder="Search by name or username..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </Form>
            </Col>
          </div>

          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="rejected-leave-table-header">
                <tr>
                  <th>SR</th>
                  <th>Name</th>
                  <th>Leave Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Applied Date</th>
                  <th>HR Approval</th>
                  <th>Admin Approval</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.length > 0 ? (
                  currentRows.map((row, index) => (
                    <tr key={index}>
                      <td>{indexOfFirstRow + index + 1}</td>
                      <td>{row.name || row.username || '-'}</td>
                      <td>{row.allowanceType || row.leaveType || '-'}</td>
                      <td>{formatDate(row.startDate)}</td>
                      <td>{formatDate(row.endDate)}</td>
                      <td>{formatDate(row.createdAt || row.appliedDate)}</td>
                      <td>{row.hrNotes || row.hrAction || 'Rejected'}</td>
                      <td>{row.adminNotes || row.adminAction || 'Rejected'}</td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={() => handleEyeClick(row)}
                        >
                          <IoEye />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      No rejected leaves found.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total Entries</td>
                  <td colSpan="8">{tableData.length}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-row d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                {Math.min(currentPage * rowsPerPage, tableData.length)} of{" "}
                {tableData.length} entries
              </div>

              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <a href="#" className="page-link" onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}>
                      Previous
                    </a>
                  </li>

                  {[...Array(totalPages)].map((_, i) => (
                    <li
                      key={i + 1}
                      className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                    >
                      <a
                        href="#"
                        className="page-link"
                        onClick={(e) => { e.preventDefault(); handlePageChange(i + 1); }}
                      >
                        {i + 1}
                      </a>
                    </li>
                  ))}

                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <a href="#" className="page-link" onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}>
                      Next
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Rejected Leave Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRowData && (
            <div className="row g-3">
              <div className="col-12"><strong>Name:</strong> {selectedRowData.name}</div>
              <div className="col-12"><strong>Leave Type:</strong> {selectedRowData.allowanceType || selectedRowData.leaveType}</div>
              <div className="col-md-6"><strong>Start Date:</strong> {formatDate(selectedRowData.startDate)}</div>
              <div className="col-md-6"><strong>End Date:</strong> {formatDate(selectedRowData.endDate)}</div>
              <div className="col-12"><strong>Reason:</strong> {selectedRowData.reason || '-'}</div>
              <div className="col-12"><strong>HR Notes:</strong> {selectedRowData.hrNotes || 'Applied twice'}</div>
              <div className="col-12"><strong>Admin Notes:</strong> {selectedRowData.adminNotes || '-'}</div>
              <div className="col-12 text-danger"><strong>Status:</strong> REJECTED</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </LeaveLayout>
  );
}